import React, { useState } from "react";
import { useAuth } from "contexts/authContext";
import { FaFileAlt, FaDownload, FaReply, FaCube } from "react-icons/fa";
import { MdInsertDriveFile } from "react-icons/md";
import md5 from "md5";

const getGravatarUrl = (email) => {
    if (!email) return 'https://www.gravatar.com/avatar/?d=mp';
    const hash = md5(email.trim().toLowerCase());
    return `https://www.gravatar.com/avatar/${hash}?d=identicon`;
};

// Utility function to construct proper image URL
const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    
    if (imageUrl.startsWith("http")) {
        return imageUrl;
    }
    
    const serverUrl = process.env.REACT_APP_API_BASE_URL;
    if (serverUrl) {
        return `${serverUrl.replace(/\/$/, "")}/${imageUrl.replace(/^\/uploads/, "uploads/images")}`;
    }
    
    return imageUrl;
};

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 KB';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const SingleMessage = ({ message, showAvatar, onReply, isTeamChat, showTimestamp }) => {
    const { user: currentUser } = useAuth();
    const isSent = message.sender.id === currentUser.id;
    const [isHovered, setIsHovered] = useState(false);

    const FilePreviewCard = ({ icon, label, fileUrl, fileName, metadata }) => (
        <div className={`flex items-center gap-3 rounded-lg p-3 min-w-0 ${isSent ? 'bg-white/20' : 'bg-gray-100 dark:bg-navy-700'}`}>
            <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-black/20`}>
                {icon}
            </div>
            <div className="flex-auto overflow-hidden min-w-0">
                <p className={`truncate text-sm font-semibold ${isSent ? 'text-white' : 'text-navy-700 dark:text-white'}`}>{fileName}</p>
                <p className={`text-xs ${isSent ? 'text-white/70' : 'text-gray-500'}`}>
                    {label} {metadata.length > 0 && `(${metadata.join(', ')})`}
                </p>
            </div>
            <a
                href={fileUrl}
                download={fileName}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 rounded-full bg-black/20 p-2 transition hover:bg-black/40"
                onClick={(e) => e.stopPropagation()}
            >
                <FaDownload className={`h-4 w-4 ${isSent ? 'text-white' : 'text-gray-700 dark:text-white'}`} />
            </a>
        </div>
    );

    const renderFileAttachment = () => {
        if (!message.fileUrl) return null;

        const isImage = message.fileType === 'image';
        const fileName = message.fileName || message.fileUrl.split('/').pop().split('?')[0].split('%2F').pop();
        
        const metadata = [];
        if (message.fileSize) {
            metadata.push(formatFileSize(message.fileSize));
        }
        if (message.filePages) {
            metadata.push(`${message.filePages} pages`);
        }

        if (isImage) {
            return (
                <div className="relative overflow-hidden rounded-lg shadow-md mt-2 max-w-full">
                    <img src={message.fileUrl} alt={fileName} className="max-h-64 object-cover w-full rounded-lg" />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/40 to-transparent flex items-end p-3 z-10`}>
                        <a
                            href={message.fileUrl}
                            download={fileName}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-white hover:underline transition-colors"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <FaDownload className="h-4 w-4" />
                            <span className="text-sm font-medium">Download Image</span>
                        </a>
                    </div>
                </div>
            );
        }

        let fileIcon;
        let fileTypeLabel;

        if (message.fileType === 'document') {
            fileIcon = <MdInsertDriveFile className={`h-5 w-5 ${isSent ? 'text-white' : 'text-brand-500'}`} />;
            fileTypeLabel = 'Document';
        } else if (message.fileType === 'model') {
            fileIcon = <FaCube className={`h-5 w-5 ${isSent ? 'text-white' : 'text-blue-500'}`} />;
            fileTypeLabel = '3D Model';
        } else {
            fileIcon = <FaFileAlt className={`h-5 w-5 ${isSent ? 'text-white' : 'text-gray-500'}`} />;
            fileTypeLabel = 'File';
        }

        return (
            <FilePreviewCard 
                icon={fileIcon} 
                label={fileTypeLabel} 
                fileUrl={message.fileUrl} 
                fileName={fileName} 
                metadata={metadata} 
            />
        );
    };

    const handleMessageClick = (e) => {
        e.stopPropagation();
        // This toggles the reply icon on click, which works for both desktop and mobile
        setIsHovered(!isHovered);
    };

    return (
        <div
            className={`group mb-1 flex items-start gap-3 ${isSent ? "justify-end" : "justify-start"}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={handleMessageClick}
        >
            {!isSent && (
                <div className="h-10 w-10 flex-shrink-0">
                    {showAvatar && (
                        <img 
                            src={getImageUrl(message.sender.imageUrl) || getGravatarUrl(message.sender.email)} 
                            alt={message.sender.firstName} 
                            className="h-full w-full rounded-full object-cover"
                            onError={(e) => {
                                e.target.onerror = null; // Prevent infinite loop
                                e.target.src = getGravatarUrl(message.sender.email);
                            }}
                        />
                    )}
                </div>
            )}
            <div className={`flex flex-col max-w-[85%] sm:max-w-[75%] lg:max-w-[60%] ${isSent ? 'items-end' : 'items-start'}`}>
                <div className="relative w-full">
                    <div className={`rounded-2xl px-3 py-2 shadow-sm ${isSent ? "rounded-br-none bg-brand-500 text-white" : "rounded-bl-none bg-white text-navy-700 dark:bg-navy-700 dark:text-white"}`}>
                        {isTeamChat && showAvatar && !isSent && (
                            <p className="mb-1 text-sm font-bold text-green-500 dark:text-green-400">
                                {message.sender.firstName} {message.sender.lastName}
                            </p>
                        )}
                        {message.repliedToMessage && (
                            <div className={`mb-2 rounded border-l-4 p-2 ${isSent ? 'border-white/50 bg-white/20' : 'border-brand-300 bg-gray-100 dark:bg-navy-600'}`}>
                                <p className={`text-sm font-semibold ${isSent ? 'text-white' : 'text-brand-500'}`}>{message.repliedToMessage.sender.firstName}</p>
                                <p className={`truncate text-sm ${isSent ? 'opacity-80' : 'text-gray-600 dark:text-gray-300'}`}>{message.repliedToMessage.content}</p>
                            </div>
                        )}
                        {renderFileAttachment()}
                        {message.content && (
                            <p className="break-words pt-2" style={{ whiteSpace: 'pre-wrap' }}>{message.content}</p>
                        )}
                    </div>
                    <div className={`absolute top-1/2 -translate-y-1/2 transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'} ${isSent ? 'left-0 -translate-x-full ml-[-8px]' : 'right-0 translate-x-full mr-[-8px]'}`}>
                        <button
                            onClick={(e) => { e.stopPropagation(); onReply(message); }}
                            className="rounded-full bg-white p-1.5 shadow-md transition-all dark:bg-navy-600"
                        >
                            <FaReply className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                        </button>
                    </div>
                </div>
                {showTimestamp && (
                    <p className={`mt-1 px-2 text-xs text-gray-400 ${isSent ? 'text-right' : 'text-left'}`}>
                        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                )}
            </div>
        </div>
    );
};

export default SingleMessage;